import { MarketingResource } from "creso-core/types";
import { ChevronRight, GalleryHorizontalEnd, ImageIcon } from "lucide-react";
import { Badge, cn } from "web-design-system";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { getResourcePreviewImage, isResourceNew } from "creso-core/utils";
import { MarketingPosterPreviewDialog } from "./marketing-poster-preview-dialog";
import { WebStoryPreviewDialog } from "./web-story-preview-dialog";

const MARKETING_CATEGORY_STYLES = {
  marketing_poster: {
    label: "Creative",
    className: "bg-tag-yellow text-tag-yellow-foreground",
    icon: ImageIcon,
  },
  web_story: {
    label: "Web Story",
    className: "bg-tag-yellow text-tag-yellow-foreground",
    icon: GalleryHorizontalEnd,
  },
} as const;

function Thumbnail({
  label,
  className,
  src,
}: {
  label: string;
  className?: string;
  src: string;
}) {
  return (
    <img src={src} alt={label} className={cn("object-cover", className)} />
  );
}

export function MarketingFeaturedCard({ item }: { item: MarketingResource }) {
  const {
    label,
    className,
    icon: Icon,
  } = MARKETING_CATEGORY_STYLES[
    item.resource_type as "marketing_poster" | "web_story"
  ];
  const [selectedResourceId, setSelectedResourceId] = useState<number | null>(
    null
  );
  const isWebStory = item.resource_type === "web_story";

  return (
    <>
      <div
        className="relative flex rounded-lg border border-border/50 bg-background overflow-hidden h-full p-2 gap-4"
        onClick={() => setSelectedResourceId(item.id)}
      >
        {isResourceNew(item) ? (
          <Badge
            variant="custom"
            className="absolute top-5 left-5 rounded-sm px-1.5 py-0.5 bg-tag-orange-foreground text-tag-orange text-xxs"
          >
            NEW
          </Badge>
        ) : null}
        <Thumbnail
          label={item.title}
          src={getResourcePreviewImage(item)}
          className="w-50 rounded-lg cursor-pointer"
        />
        <div className="flex flex-col gap-4 py-3">
          <Badge
            variant="custom"
            className={cn("rounded-sm px-2 py-1 w-fit gap-1", className)}
          >
            <Icon className="size-4" />
            {label}
          </Badge>
          <div className="text-lg font-medium">{item.title}</div>
          <Link
            to="/resources/marketing"
            search={{ category: isWebStory ? "web_story" : "marketing_poster" }}
            className="flex items-center gap-0.5 text-sm text-foreground w-fit"
          >
            View All
            <ChevronRight className="size-4" />
          </Link>
        </div>
      </div>
      {isWebStory ? (
        <WebStoryPreviewDialog
          items={[item]}
          selectedId={selectedResourceId}
          onSelectedIdChange={setSelectedResourceId}
        />
      ) : (
        <MarketingPosterPreviewDialog
          items={[item]}
          selectedId={selectedResourceId}
          onSelectedIdChange={setSelectedResourceId}
        />
      )}
    </>
  );
}
